import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faArrowLeft,
  faInbox,
  faSortDown,
  faSortUp,
  faUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { SurveyQuestionType, SurveyResponse } from '../../types';
import { API_BASE_URL } from '../../config/api';
import SurveyResultsFilters, {
  applySurveyResultsFilters,
  emptySurveyResultsFilters,
  surveyResultsFiltersFromSearch,
  surveyResultsFiltersToSearch,
} from './SurveyResultsFilters';
import SurveyUserHover from './SurveyUserHover';

const PAGE_SIZE = 20;

const formatDate = (value?: string | Date | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatAnswer = (value: string, type?: SurveyQuestionType) => {
  if (!value) return '—';
  if (type === 'boolean') {
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
  }
  return value;
};

const formatPhonePlatform = (platform?: string) => {
  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';
  return '—';
};

const truncate = (value: string, max = 80) => {
  const text = String(value || '').trim();
  if (!text) return '—';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

interface QuestionMeta {
  questionId: string;
  prompt: string;
  type: SurveyQuestionType;
}

const SurveyQuestionResponsesPage: React.FC = () => {
  const { surveyId, questionId } = useParams<{ surveyId: string; questionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [surveyName, setSurveyName] = useState('');
  const [question, setQuestion] = useState<QuestionMeta | null>(null);
  const [results, setResults] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [sortColumn, setSortColumn] = useState('completedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [filters, setFilters] = useState(surveyResultsFiltersFromSearch(searchParams));

  const loadResults = useCallback(async (page: number) => {
    if (!surveyId || !questionId) return;
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page,
        limit: PAGE_SIZE,
        sortColumn,
        sortDirection,
      };
      applySurveyResultsFilters(params, searchParams);

      const response = await axios.get(
        `${API_BASE_URL}/surveys/${surveyId}/results/questions/${questionId}`,
        { withCredentials: true, params }
      );
      setSurveyName(response.data.survey?.name || '');
      setQuestion(response.data.question || null);
      setResults(response.data.results || []);
      setTotalResults(response.data.totalResults || 0);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(response.data.currentPage || page);
      setError(null);
    } catch (err: any) {
      console.error('Error loading question responses:', err);
      setError(err.response?.data?.message || 'Failed to load question responses');
    } finally {
      setLoading(false);
    }
  }, [questionId, searchParams, sortColumn, sortDirection, surveyId]);

  useEffect(() => {
    setFilters(surveyResultsFiltersFromSearch(searchParams));
    loadResults(1);
  }, [loadResults, searchParams]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchParams(surveyResultsFiltersToSearch(filters));
  };

  const handleClearSearch = () => {
    setFilters(emptySurveyResultsFilters());
    setSearchParams({});
  };

  const handleSortChange = (column: string) => {
    if (column === sortColumn) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'completedAt' ? 'desc' : 'asc');
  };

  const renderSortIndicator = (column: string) => {
    const upColor = column === sortColumn && sortDirection === 'asc' ? '#333333' : '#dadee3';
    const downColor = column === sortColumn && sortDirection === 'desc' ? '#333333' : '#dadee3';
    return (
      <span className="ms-1" style={{ display: 'inline-block', position: 'relative', width: '10px', height: '14px' }}>
        <FontAwesomeIcon icon={faSortUp} style={{ color: upColor, position: 'absolute', top: '-1px', left: 0 }} />
        <FontAwesomeIcon icon={faSortDown} style={{ color: downColor, position: 'absolute', bottom: '-2px', left: 0 }} />
      </span>
    );
  };

  const hasFilters = Array.from(searchParams.keys()).length > 0;
  const resultsQuery = searchParams.toString();
  const backTo = surveyId
    ? `/surveys/${surveyId}/results${resultsQuery ? `?${resultsQuery}` : ''}`
    : '/surveys';

  return (
    <div className="container-fluid mt-3 mb-5 survey-results-page">
      <div className="mb-3">
        <Link to={backTo} className="btn btn-sm btn-outline-secondary">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to results
        </Link>
      </div>

      <div className="d-flex align-items-center gap-2 mb-3">
        <FontAwesomeIcon icon={faInbox} className="text-primary" />
        <h2 className="mb-0">All Responses{surveyName ? ` — ${surveyName}` : ''}</h2>
      </div>

      <section className="card shadow-sm border-0 survey-question-hero">
        <div className="card-body">
          <h3 className="survey-question-hero-title">
            <strong>Question:</strong> {question?.prompt || 'Open-ended question'}
          </h3>
        </div>
      </section>

      <section className="card shadow-sm border-0 survey-reports-section">
        <div className="card-body">
          <h5 className="card-title mb-3">Answers</h5>
          <SurveyResultsFilters
            email={filters.email}
            firstName={filters.firstName}
            lastName={filters.lastName}
            serial={filters.serial}
            platform={filters.platform}
            status={filters.status}
            dateStart={filters.dateStart}
            dateEnd={filters.dateEnd}
            onEmail={(email) => setFilters((current) => ({ ...current, email }))}
            onFirstName={(firstName) => setFilters((current) => ({ ...current, firstName }))}
            onLastName={(lastName) => setFilters((current) => ({ ...current, lastName }))}
            onSerial={(serial) => setFilters((current) => ({ ...current, serial }))}
            onPlatform={(platform) => setFilters((current) => ({ ...current, platform }))}
            onStatus={(status) => setFilters((current) => ({ ...current, status }))}
            onDateStart={(dateStart) => setFilters((current) => ({ ...current, dateStart }))}
            onDateEnd={(dateEnd) => setFilters((current) => ({ ...current, dateEnd }))}
            onSubmit={handleSearch}
            onClear={handleClearSearch}
          />

          {error ? <div className="alert alert-danger">{error}</div> : null}

          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '220px' }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <table className="table table-striped survey-results-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSortChange('user')} style={{ cursor: 'pointer' }}>
                      User {renderSortIndicator('user')}
                    </th>
                    <th onClick={() => handleSortChange('completedAt')} style={{ cursor: 'pointer' }}>
                      Completed {renderSortIndicator('completedAt')}
                    </th>
                    <th>Phone</th>
                    <th>Devices</th>
                    <th>Answer</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.length > 0 ? (
                    results.map((result) => {
                      const devices = (result.deviceSerials || []).join(', ');
                      const answer = result.questionResponses[0];
                      return (
                        <tr key={result._id}>
                          <td>
                            <SurveyUserHover result={result} />
                          </td>
                          <td className="text-nowrap">{formatDate(result.completedAt)}</td>
                          <td>{formatPhonePlatform(result.phonePlatform)}</td>
                          <td title={devices}>{devices ? truncate(devices, 24) : '—'}</td>
                          <td>
                            <div className="survey-answer-card" title={answer?.value || ''}>
                              {formatAnswer(answer?.value || '', answer?.type || question?.type)}
                              {answer?.additionalInfo ? (
                                <div className="survey-answer-card-extra">{answer.additionalInfo}</div>
                              ) : null}
                            </div>
                          </td>
                          <td className="text-nowrap">
                            <Link
                              className="btn btn-outline-primary btn-sm"
                              to={`/surveys/${surveyId}/results/${result._id}`}
                              title="Full report"
                            >
                              Full Report <FontAwesomeIcon icon={faUpRightFromSquare} className="ms-1" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        {hasFilters ? 'No responses found matching the current filters' : 'No answers yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {totalPages > 0 && totalResults > 0 ? (
                <div className="responsive-pagination d-flex justify-content-between align-items-center mt-2">
                  <div className="fw-bold pt-1">
                    {totalResults.toLocaleString()} total response{totalResults === 1 ? '' : 's'}
                  </div>
                  <nav aria-label="Question response pagination">
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadResults(1)} disabled={currentPage === 1} aria-label="First page">
                          <FontAwesomeIcon icon={faAnglesLeft} />
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadResults(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page">
                          <FontAwesomeIcon icon={faAngleLeft} />
                        </button>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link">
                          Page {currentPage} of {totalPages}
                        </span>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadResults(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page">
                          <FontAwesomeIcon icon={faAngleRight} />
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadResults(totalPages)} disabled={currentPage === totalPages} aria-label="Last page">
                          <FontAwesomeIcon icon={faAnglesRight} />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default SurveyQuestionResponsesPage;
